import { useCallback, useEffect, useState } from "react";
import { api, forceLogout } from "../api/client";
import { useAppStore } from "../store/appStore";
import type { User } from "../store/appStore";

export type { User };

let cachedUser: User | null = null;
let fetchPromise: Promise<User | null> | null = null;

const OPS_ROLES = ["superadmin", "operator"];

interface ApiError {
  response?: { status?: number };
}

/** Fetch the current user from /auth/me. Returns null if not authenticated. */
export async function fetchCurrentUser(): Promise<User | null> {
  const token = localStorage.getItem("lk_ops_access");
  if (!token) {
    cachedUser = null;
    return null;
  }
  if (cachedUser) return cachedUser;
  if (fetchPromise) return fetchPromise;
  fetchPromise = api
    .get<User>("/auth/me")
    .then((res) => {
      if (!OPS_ROLES.includes(res.data.role)) {
        cachedUser = null;
        forceLogout();
        return null;
      }
      cachedUser = res.data;
      return res.data;
    })
    .catch((err: unknown) => {
      cachedUser = null;
      const ae = err as ApiError;
      if (ae.response?.status === 401) forceLogout();
      return null;
    })
    .finally(() => {
      fetchPromise = null;
    });
  return fetchPromise;
}

export function clearCurrentUser() {
  cachedUser = null;
}

/** React hook that provides the current user + loading state. */
export function useCurrentUser() {
  const storeSetUser = useAppStore((s) => s.setUser);
  const [user, setUser] = useState<User | null>(cachedUser);
  const [loading, setLoading] = useState(!cachedUser);

  const refresh = useCallback(async () => {
    setLoading(true);
    const u = await fetchCurrentUser();
    setUser(u);
    storeSetUser(u);
    setLoading(false);
    return u;
  }, [storeSetUser]);

  useEffect(() => {
    if (cachedUser) {
      setUser(cachedUser);
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  return { user, loading, refresh, setUser };
}
